import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import { createPublicPlaybackSession, resolvePublicShare } from '../service'
import VideoJS from '../components/video/VideoJS'

// Watch page for a link the owner opened to viewers without a login (owner decision D14).
export default function PublicShare () {
  const { token } = useParams()
  const [share, setShare] = useState(null)
  const [playbackUrl, setPlaybackUrl] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    // No-login pages are never indexed (interim minors policy, owner decision D7).
    const robots = document.createElement('meta')
    robots.name = 'robots'
    robots.content = 'noindex, nofollow'
    document.head.appendChild(robots)
    return () => robots.remove()
  }, [])

  useEffect(() => {
    let cancelled = false
    let retryTimer = null
    let refreshTimer = null
    let polls = 0

    // The playback cookie is short-lived; renew it (rechecking revocation) before it expires.
    const scheduleRefresh = expiresAt => {
      const delay = new Date(expiresAt).getTime() - Date.now() - 60000
      if (!Number.isFinite(delay)) return
      refreshTimer = window.setTimeout(async () => {
        // One retry, so a brief network error isn't reported as a revoked link.
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            const playback = await createPublicPlaybackSession(token)
            if (!cancelled) scheduleRefresh(playback.expiresAt)
            return
          } catch (err) {
            console.error(err)
            if (attempt === 0) await new Promise(resolve => window.setTimeout(resolve, 3000))
          }
        }
        if (!cancelled) setError('This shared link is invalid, expired, or has been revoked.')
      }, Math.max(delay, 5000))
    }

    const load = async () => {
      try {
        const result = await resolvePublicShare(token, { poll: polls > 0 })
        if (cancelled) return
        setShare(result)

        if (result.playback_ready) {
          const playback = await createPublicPlaybackSession(token)
          if (cancelled) return
          setPlaybackUrl(playback.url)
          scheduleRefresh(playback.expiresAt)
          return
        }

        if (
          result.allow_playback &&
          result.highlight &&
          ['pending', 'submitting', 'processing'].includes(result.clip_status)
        ) {
          polls += 1
          // Stop after about 15 minutes; a clip that is still not ready needs the owner.
          if (polls < 180) retryTimer = window.setTimeout(load, 5000)
        }
      } catch (err) {
        console.error(err)
        if (!cancelled) setError('This shared link is invalid, expired, or has been revoked.')
      }
    }

    load()
    return () => {
      cancelled = true
      if (retryTimer) window.clearTimeout(retryTimer)
      if (refreshTimer) window.clearTimeout(refreshTimer)
    }
  }, [token])

  if (error) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  if (!share) {
    return (
      <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress aria-label="Loading shared video" />
      </Box>
    )
  }

  const { upload, highlight } = share
  const title = highlight ? highlight.name : upload.name
  const signInPath = highlight ? `/upload/${upload.id}/highlight/${highlight.id}` : `/upload/${upload.id}`

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Stack spacing={2}>
        <Typography variant="h4">{title}</Typography>
        {highlight
          ? <Typography color="text.secondary">From {upload.name}</Typography>
          : null}

        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          {playbackUrl
            ? (
              <Box sx={{ bgcolor: 'black' }}>
                <VideoJS
                  options={{
                    autoplay: false,
                    controls: true,
                    responsive: true,
                    fluid: true,
                    preload: 'metadata',
                    aspectRatio: '16:9',
                    sources: playbackUrl,
                    withCredentials: true,
                    html5: {
                      vhs: {
                        withCredentials: true
                      }
                    }
                  }}
                />
              </Box>
              )
            : upload.thumbnail_url
              ? (
                <Box
                  component="img"
                  src={upload.thumbnail_url}
                  alt=""
                  sx={{ width: '100%', maxHeight: 520, objectFit: 'contain', bgcolor: 'black' }}
                />
                )
              : null}

          <Stack spacing={1.5} sx={{ p: 2 }}>
            {highlight?.description || upload.description
              ? <Typography>{highlight?.description || upload.description}</Typography>
              : null}

            {!share.allow_playback
              ? (
                <Alert
                  severity="info"
                  action={<Button color="inherit" size="small" href={signInPath}>Sign in</Button>}
                >
                  This link shows a preview only. Sign in to Highlights with access to watch.
                </Alert>
                )
              : null}

            {share.allow_playback && highlight && !playbackUrl
              ? (
                <Alert severity={share.clip_status === 'failed' ? 'error' : 'info'}>
                  {share.clip_status === 'failed'
                    ? 'This shared clip could not be prepared.'
                    : 'Preparing the shared clip. Playback will start when it is ready.'}
                </Alert>
                )
              : null}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  )
}
